'use client';

import { Button, ButtonProps, Text } from '@bridebook/aura';
import { RoundedModalContent } from '../rounded-modal/rounded-modal-content';
import { RoundedModalFooter } from '../rounded-modal/rounded-modal-footer';
import { RoundedModalHeader, RoundedModalTitle } from '../rounded-modal/rounded-modal-header';
import { RoundedModalHeaderIllustration } from '../rounded-modal/rounded-modal-header-illustration';
import { useRoundedModalContainerContext } from '../rounded-modal/rounded-modal-container-context';
import { ComponentProps, ReactNode } from 'react';
import { cva } from 'class-variance-authority';

export type ConfirmationModalContentProps = {
  title: string;
  text?: string | ReactNode;
  /**
   * Optional illustration above the title. When set (including `null`), replaces the illustration
   * implied by `variant` (e.g. destructive alert). When omitted, the variant default is used.
   */
  headerIllustration?: ReactNode;
  primaryButtonProps?: ComponentProps<typeof Button>;
  secondaryButtonProps?: ComponentProps<typeof Button>;
  showCloseButton?: boolean;
  variant?: 'primary' | 'destructive' | 'destructive-no-image';
  headerTitleVariant?: ComponentProps<typeof RoundedModalTitle>['variant'];
};

type VariantToButtonVariant = {
  first: ButtonProps['variant'];
  second: ButtonProps['variant'];
  illustration?: ReactNode;
};

const variantToButtonVariant: Record<
  NonNullable<ConfirmationModalContentProps['variant']>,
  VariantToButtonVariant
> = {
  primary: { first: 'primary', second: 'secondary' },
  destructive: {
    first: 'destructive',
    second: 'tertiary',
    illustration: <RoundedModalHeaderIllustration variant="alert" />,
  },
  'destructive-no-image': { first: 'destructive', second: 'tertiary', illustration: null },
};

/**
 * Extra classnames for RoundedModalFooter. Used to reverse the buttons order for standard and lg widths so the primary button is on the right.
 */
const footerExtraClassNameVariants = cva('', {
  variants: {
    width: {
      'responsive-sm': '',
      sm: '',
      standard: 'flex-row-reverse',
      lg: 'flex-row-reverse',
    },
  },
});

/**
 * Content component for confirmation modals using aura components.
 * Provides a consistent layout with title, optional text, and action buttons.
 */
export const ConfirmationModalContent = ({
  title,
  text,
  headerIllustration,
  primaryButtonProps,
  secondaryButtonProps,
  showCloseButton = false,
  variant = 'primary',
  headerTitleVariant = 'coupleside',
}: ConfirmationModalContentProps) => {
  const illustrationFromVariant = variantToButtonVariant[variant].illustration;
  const headerIllustrationNode =
    headerIllustration !== undefined ? headerIllustration : illustrationFromVariant;

  const { width } = useRoundedModalContainerContext();

  return (
    <>
      <RoundedModalHeader showCloseButton={showCloseButton}>
        {headerIllustrationNode}
        <RoundedModalTitle variant={headerTitleVariant}>{title}</RoundedModalTitle>
      </RoundedModalHeader>
      {text && (
        <RoundedModalContent className="text-center">
          {typeof text === 'string' ? (
            <Text variant="typo-base" className="text-center text-space">
              {text}
            </Text>
          ) : (
            text
          )}
        </RoundedModalContent>
      )}
      <RoundedModalFooter className={footerExtraClassNameVariants({ width })}>
        {primaryButtonProps && (
          <Button
            variant={variantToButtonVariant[variant].first}
            className="min-w-[140px]"
            {...primaryButtonProps}
          />
        )}
        {secondaryButtonProps && (
          <Button
            variant={variantToButtonVariant[variant].second}
            className="min-w-[140px]"
            {...secondaryButtonProps}
          />
        )}
      </RoundedModalFooter>
    </>
  );
};
